import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconFile, IconDownload, IconTrash, IconRefresh, IconSearch,
  IconUpload, IconX, IconPhoto, IconFolder
} from '@tabler/icons-react';
import api from '../api/api';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

function formatDate(str) {
  if (!str) return '-';
  const d = new Date(str);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getFileIcon(contentType) {
  if (!contentType) return <IconFile size={16} />;
  if (contentType.startsWith('image/')) return <IconPhoto size={16} className="text-primary" />;
  if (contentType === 'application/pdf') return <IconFile size={16} className="text-danger" />;
  return <IconFile size={16} />;
}

export default function FileManagerPage({ me, showToast }) {
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState({ totalCount: 0, totalSizeMB: '0.00' });
  const [statsLoading, setStatsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const dragZoneRef = useRef(null);

  const [previewFile, setPreviewFile] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const isMaster = me?.role === 'MASTER';

  // 파일 목록 조회
  const fetchFiles = useCallback(async (p = 0, search = '') => {
    setLoading(true);
    try {
      const res = await api.get('/files/admin', {
        params: { page: p, size: 20, search: search || undefined }
      });
      setFiles(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
      setTotalElements(res.data.totalElements || 0);
      setPage(p);
    } catch (err) {
      showToast('파일 목록 조회 실패', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // 통계 조회
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/files/admin/stats');
      setStats({
        totalCount: res.data.totalCount || 0,
        totalSizeMB: res.data.totalSizeMB || '0.00'
      });
    } catch (err) {
      // 통계 조회 실패는 무시
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles(0, searchTerm);
    fetchStats();
  }, []);

  // 검색 처리 (debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFiles(0, searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchFiles]);

  // 파일 선택 처리
  const handleFileSelect = async (selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    const now = Date.now();

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      if (file.size > 10 * 1024 * 1024) {
        showToast(`"${file.name}" 파일이 10MB를 초과합니다.`, 'warning');
        continue;
      }

      const tempId = now + i;
      setUploadFiles(prev => [...prev, {
        tempId,
        name: file.name,
        size: file.size,
        uploading: true
      }]);

      try {
        const formData = new FormData();
        formData.append('files', file);
        const res = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const uploaded = res.data[0];

        setUploadFiles(prev => prev.map(f =>
          f.tempId === tempId ? { ...f, id: uploaded.id, uploading: false } : f
        ));
      } catch (err) {
        setUploadFiles(prev => prev.map(f =>
          f.tempId === tempId ? { ...f, uploading: false, error: '업로드 실패' } : f
        ));
        showToast(`"${file.name}" 업로드 실패`, 'danger');
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (dragZoneRef.current) {
      dragZoneRef.current.style.borderColor = '#0d6efd';
      dragZoneRef.current.style.backgroundColor = '#f0f7ff';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (dragZoneRef.current) {
      dragZoneRef.current.style.borderColor = '#ccc';
      dragZoneRef.current.style.backgroundColor = 'transparent';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (dragZoneRef.current) {
      dragZoneRef.current.style.borderColor = '#ccc';
      dragZoneRef.current.style.backgroundColor = 'transparent';
    }
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const removeFile = (tempId) => {
    setUploadFiles(prev => prev.filter(f => f.tempId !== tempId));
  };

  const handleDownload = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      showToast('파일 다운로드 실패', 'danger');
    }
  };

  const handlePreview = (file) => {
    if (file.contentType && file.contentType.startsWith('image/')) {
      setPreviewFile(file);
      setPreviewModalOpen(true);
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`"${file.originalName}" 파일을 삭제하시겠습니까?`)) return;

    try {
      await api.delete(`/files/admin/${file.id}`);
      showToast('파일이 삭제되었습니다.', 'success');
      fetchFiles(page, searchTerm);
      fetchStats();
    } catch (err) {
      showToast('파일 삭제 실패', 'danger');
    }
  };

  const handleCompleteUpload = () => {
    setUploadFiles([]);
    fetchFiles(0, searchTerm);
    fetchStats();
    showToast('파일 업로드 완료', 'success');
  };

  return (
    <div className="page-body">
      <div className="container-xl">
        {/* 헤더 */}
        <div className="page-wrapper">
          <div className="page-header d-print-none">
            <div className="container-xl">
              <div className="row align-items-center">
                <div className="col">
                  <h2 className="page-title d-flex align-items-center gap-2">
                    <IconFolder size={28} />파일 관리
                  </h2>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 통계 */}
        <div className="row mb-3">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="mb-1">전체 파일</h4>
                    <div className="text-muted">
                      {statsLoading ? '조회 중...' : `${stats.totalCount}개`}
                    </div>
                  </div>
                  <div className="text-muted">
                    <IconFile size={32} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="mb-1">전체 용량</h4>
                    <div className="text-muted">
                      {statsLoading ? '조회 중...' : `${stats.totalSizeMB} MB`}
                    </div>
                  </div>
                  <div className="text-muted">
                    <IconFolder size={32} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 업로드 영역 */}
        <div className="card mb-3">
          <div className="card-header">
            <h3 className="card-title">파일 업로드</h3>
          </div>
          <div className="card-body">
            <div
              ref={dragZoneRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                border: '2px dashed #ccc',
                borderRadius: 8,
                padding: '30px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
            >
              <div className="mb-2">
                <IconUpload size={40} className="text-muted" />
              </div>
              <p className="mb-0">
                <strong>파일을 여기로 드래그하거나</strong>
              </p>
              <button
                type="button"
                className="btn btn-primary btn-sm mt-2"
                onClick={() => fileInputRef.current?.click()}
              >
                파일 선택
              </button>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={(e) => handleFileSelect(e.target.files)}
                style={{ display: 'none' }}
              />
              <p className="text-muted small mt-2 mb-0">
                최대 5개 · 파일당 10MB 이하
              </p>
            </div>

            {uploadFiles.length > 0 && (
              <div className="mt-3">
                <h5>업로드 대기 중:</h5>
                {uploadFiles.map(f => (
                  <div key={f.tempId} className="d-flex align-items-center gap-2 py-2 px-2 border-bottom">
                    <IconFile size={14} className="text-muted flex-shrink-0" />
                    <span className="flex-fill text-truncate small">{f.name}</span>
                    <small className="text-muted flex-shrink-0">
                      {(f.size / 1024 / 1024).toFixed(2)} MB
                    </small>
                    {f.uploading && (
                      <span className="spinner-border spinner-border-sm text-primary flex-shrink-0"
                        style={{ width: 14, height: 14 }} />
                    )}
                    {f.error && (
                      <span className="badge bg-danger-lt text-danger flex-shrink-0" style={{ fontSize: 10 }}>
                        실패
                      </span>
                    )}
                    {!f.uploading && !f.error && (
                      <button className="btn btn-sm btn-ghost-danger" onClick={() => removeFile(f.tempId)}>
                        <IconX size={14} />
                      </button>
                    )}
                  </div>
                ))}
                {uploadFiles.every(f => !f.uploading) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary mt-2"
                    onClick={handleCompleteUpload}
                  >
                    완료
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 검색 및 필터 */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="input-icon">
              <IconSearch size={16} className="input-icon-addon" />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="파일명 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* 파일 목록 */}
        <div className="card">
          <div className="table-responsive">
            <table className="table table-vcenter card-table table-striped">
              <thead>
                <tr>
                  <th style={{ whiteSpace: 'nowrap' }}>파일명</th>
                  <th style={{ whiteSpace: 'nowrap' }}>크기</th>
                  <th style={{ whiteSpace: 'nowrap' }}>업로더</th>
                  <th style={{ whiteSpace: 'nowrap' }}>업로드일</th>
                  <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4">로딩 중...</td>
                  </tr>
                ) : files.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">파일이 없습니다.</td>
                  </tr>
                ) : files.map(f => (
                  <tr key={f.id}>
                    <td style={{ whiteSpace: 'nowrap', maxWidth: 300 }}>
                      <div className="d-flex align-items-center gap-2">
                        {getFileIcon(f.contentType)}
                        <span className="text-truncate" title={f.originalName}>{f.originalName}</span>
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {((f.fileSize || 0) / 1024 / 1024).toFixed(2)} MB
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{f.uploaderName}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                      {formatDate(f.createdAt)}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                      <div className="d-flex gap-1 justify-content-center">
                        <button
                          className="btn btn-sm btn-ghost-primary"
                          onClick={() => handleDownload(f)}
                          title="다운로드"
                        >
                          <IconDownload size={16} />
                        </button>
                        {f.contentType && f.contentType.startsWith('image/') && (
                          <button
                            className="btn btn-sm btn-ghost-info"
                            onClick={() => handlePreview(f)}
                            title="미리보기"
                          >
                            <IconPhoto size={16} />
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-ghost-danger"
                          onClick={() => handleDelete(f)}
                          title="삭제"
                        >
                          <IconTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="card-footer d-flex justify-content-center">
              <ul className="pagination mb-0">
                <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => fetchFiles(page - 1, searchTerm)}>
                    이전
                  </button>
                </li>
                {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                  const startP = Math.max(0, Math.min(page - 4, totalPages - 10));
                  const p = startP + i;
                  if (p >= totalPages) return null;
                  return (
                    <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => fetchFiles(p, searchTerm)}>
                        {p + 1}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => fetchFiles(page + 1, searchTerm)}>
                    다음
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* 이미지 미리보기 모달 */}
      {previewModalOpen && previewFile && (
        <>
          <div className="modal-backdrop fade show" />
          <div
            className="modal modal-blur fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
            onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, () => setPreviewModalOpen(false))}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{previewFile.originalName}</h5>
                  <button type="button" className="btn-close" onClick={() => setPreviewModalOpen(false)} />
                </div>
                <div className="modal-body text-center">
                  <img
                    src={`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/files/image/${previewFile.id}`}
                    alt={previewFile.originalName}
                    style={{ maxWidth: '100%', maxHeight: 500 }}
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary btn-sm" onClick={() => setPreviewModalOpen(false)}>
                    닫기
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      handleDownload(previewFile);
                      setPreviewModalOpen(false);
                    }}
                  >
                    다운로드
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
